/**
 * Snowfall Effect - Two-layer snowflakes animation for Vue
 * Active period: December 7 - January 10 (festive season)
 */

class SnowfallEffect {
  constructor(container) {
    this.container = container;
    this.canvases = { back: null, front: null };
    this.snowflakes = { back: [], front: [] };
    this.animationId = null;
    this.config = {
      countBack: 35, 
      countFront: 15,
      size: [2, 8], 
      speed: [0.08, 0.3], 
      wind: [-0.3, 0.3]
    };
    
    if (this.isFestivePeriod()) {
      this.init();
      console.log('❄️ Snowfall: Active (festive period)');
    }
  }
  
  isFestivePeriod() {
    const now = new Date();
    const month = now.getMonth();
    const day = now.getDate();
    return (month === 11 && day >= 7) || (month === 0 && day <= 10);
  }
  
  init() {
    // Удаляем старые canvas если есть
    document.querySelectorAll('canvas[id^="snowfall"]').forEach(c => c.remove());
    
    // Убедимся что контейнер имеет position: relative
    if (window.getComputedStyle(this.container).position === 'static') {
      this.container.style.position = 'relative';
    }
    
    // Создаём два слоя снежинок
    this.canvases.back = this.createCanvas('snowfall-back', 1);
    this.canvases.front = this.createCanvas('snowfall-front', 9998);
    this.resize();
    
    window.addEventListener('resize', () => this.resize());
    
    // Создаём снежинки
    for (let i = 0; i < this.config.countBack; i++) {
      this.snowflakes.back.push(this.createFlake());
    }
    for (let i = 0; i < this.config.countFront; i++) {
      this.snowflakes.front.push(this.createFlake());
    }
    
    this.start();
  }
  
  createCanvas(id, zIndex) {
    const canvas = document.createElement('canvas');
    canvas.id = id;
    canvas.style.cssText = `position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none!important;z-index:${zIndex}`;
    this.container.appendChild(canvas);
    return canvas;
  }
  
  resize() {
    const { width, height } = this.container.getBoundingClientRect();
    Object.values(this.canvases).forEach(canvas => {
      canvas.width = width;
      canvas.height = height;
    });
    
    // Redraw immediately after resize to prevent flicker
    if (this.animationId) {
      const ctxBack = this.canvases.back.getContext('2d');
      const ctxFront = this.canvases.front.getContext('2d');
      this.snowflakes.back.forEach(f => this.draw(ctxBack, f));
      this.snowflakes.front.forEach(f => this.draw(ctxFront, f));
    }
  }
  
  createFlake(atTop = false) {
    const x = Math.random() * this.canvases.back.width;
    const size = this.rand(...this.config.size);
    const sizeRatio = size / this.config.size[1];
    const hasSwing = Math.random() < 0.15;
    
    // Определяем тему (dark/light)
    const isDarkTheme = document.body.classList.contains('dark-mode');
    const isColored = isDarkTheme ? Math.random() < 0.1 : true; // В светлой теме все цветные
    
    return {
      x, 
      currentX: x,
      y: atTop ? -10 : Math.random() * this.canvases.back.height,
      size, 
      type: Math.random() < 0.5 ? 0 : 1,
      speed: this.rand(...this.config.speed) * (0.5 + sizeRatio * 0.5),
      wind: this.rand(...this.config.wind),
      opacity: this.rand(0.5, 1) * (0.6 + sizeRatio * 0.4),
      swing: Math.random() * Math.PI * 2,
      swingSpeed: hasSwing ? this.rand(0.01, 0.03) : 0,
      swingAmplitude: hasSwing ? (10 + size * 2) : 0,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: this.rand(-0.01, 0.01),
      twinkle: Math.random() * Math.PI * 2,
      twinkleSpeed: this.rand(0.015, 0.03),
      color: isColored ? ['#667eea', '#764ba2', '#4ade80'][Math.floor(Math.random() * 3)] : '#fff',
      colorPhase: Math.random() * Math.PI * 2,
      colorPhaseSpeed: isColored ? this.rand(0.008, 0.015) : 0
    };
  }
  
  rand(min, max) {
    return Math.random() * (max - min) + min;
  }
  
  update(flake) {
    flake.y += flake.speed;
    flake.x += flake.wind;
    
    if (flake.swingAmplitude > 0) {
      if (Math.random() < 0.001) {
        flake.swingAmplitude *= 0.95;
        if (flake.swingAmplitude < 0.5) flake.swingAmplitude = flake.swingSpeed = 0;
      }
      flake.swing += flake.swingSpeed;
      flake.currentX = flake.x + Math.sin(flake.swing) * flake.swingAmplitude;
    } else {
      flake.currentX = flake.x;
    }
    
    flake.rotation += flake.rotationSpeed;
    flake.twinkle += flake.twinkleSpeed;
    flake.colorPhase += flake.colorPhaseSpeed;
    
    if (flake.y > this.canvases.back.height + 10) return this.createFlake(true);
    if (flake.currentX < -10) flake.x = this.canvases.back.width + 10;
    if (flake.currentX > this.canvases.back.width + 10) flake.x = -10;
    
    return flake;
  }
  
  drawBranches(ctx, positions, flake, hexRadius, hasSubBranches = false) {
    const angle = Math.PI / 3;
    positions.forEach((pos, idx) => {
      const y = -hexRadius - (flake.size - hexRadius) * pos;
      const len = flake.size * (0.28 - idx * 0.04);
      
      [-1, 1].forEach(dir => {
        const endX = dir * len * Math.sin(angle);
        const endY = y - len * Math.cos(angle);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(endX, endY);
        ctx.stroke();
        
        if (hasSubBranches && idx < 2 && len > flake.size * 0.2) {
          const subLen = len * 0.4;
          const midX = endX * 0.6;
          const midY = y - (y - endY) * 0.6;
          ctx.beginPath();
          ctx.moveTo(midX, midY);
          ctx.lineTo(midX + dir * subLen * 0.5, midY - subLen * 0.5);
          ctx.stroke();
        }
      });
    });
  }
  
  draw(ctx, flake) {
    ctx.save();
    ctx.translate(flake.currentX, flake.y);
    ctx.rotate(flake.rotation);
    ctx.globalAlpha = flake.opacity * (0.7 + Math.sin(flake.twinkle) * 0.3);
    
    let currentColor = flake.color;
    if (flake.colorPhaseSpeed > 0) {
      const colorMix = (Math.sin(flake.colorPhase) + 1) / 2;
      if (colorMix >= 0.5) {
        const t = (colorMix - 0.5) * 2;
        const rgb = this.hexToRgb(flake.color);
        currentColor = `rgb(${Math.round(rgb.r + (255 - rgb.r) * t)},${Math.round(rgb.g + (255 - rgb.g) * t)},${Math.round(rgb.b + (255 - rgb.b) * t)})`;
      }
    }
    
    ctx.strokeStyle = ctx.fillStyle = currentColor;
    ctx.lineWidth = Math.min(0.6, Math.max(0.3, flake.size * 0.03));
    
    const hexRadius = flake.size * 0.15;
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI) / 3;
      const x = hexRadius * Math.cos(angle);
      const y = hexRadius * Math.sin(angle);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
    
    for (let i = 0; i < 6; i++) {
      ctx.save();
      ctx.rotate((i * Math.PI) / 3);
      ctx.beginPath();
      ctx.moveTo(0, -hexRadius);
      ctx.lineTo(0, -flake.size);
      ctx.stroke();
      
      const positions = flake.type === 0 ? [0.4, 0.6, 0.85] : [0.35, 0.55, 0.75, 0.9];
      this.drawBranches(ctx, positions, flake, hexRadius, flake.type === 1);
      
      ctx.restore();
    }
    
    ctx.restore();
  }
  
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 255, g: 255, b: 255 };
  }
  
  animate() {
    if (!this.animationId) return;
    
    this.snowflakes.back = this.snowflakes.back.map(f => this.update(f));
    this.snowflakes.front = this.snowflakes.front.map(f => this.update(f));
    
    const ctxBack = this.canvases.back.getContext('2d');
    const ctxFront = this.canvases.front.getContext('2d');
    
    ctxBack.clearRect(0, 0, this.canvases.back.width, this.canvases.back.height);
    ctxFront.clearRect(0, 0, this.canvases.front.width, this.canvases.front.height);
    
    this.snowflakes.back.forEach(f => this.draw(ctxBack, f));
    this.snowflakes.front.forEach(f => this.draw(ctxFront, f));
    
    this.animationId = requestAnimationFrame(() => this.animate());
  }
  
  start() {
    if (!this.animationId) {
      this.animationId = true;
      this.animate();
    }
  }
  
  stop() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }
  
  destroy() {
    this.stop();
    Object.values(this.canvases).forEach(canvas => canvas?.parentNode?.removeChild(canvas));
    document.querySelectorAll('canvas[id^="snowfall"]').forEach(c => c.remove());
  }
}

// Vue composable
export function useSnowfall() {
  let snowfallEffect = null;

  const initSnowfall = (container) => {
    try {
      if (container && !snowfallEffect) {
        snowfallEffect = new SnowfallEffect(container);
      }
    } catch (error) {
      console.error('[Snowfall] Failed to initialize:', error);
    }
  };

  const destroySnowfall = () => {
    if (snowfallEffect) {
      snowfallEffect.destroy();
      snowfallEffect = null;
    }
  };

  return {
    initSnowfall,
    destroySnowfall
  };
}

